import { Injectable } from '@angular/core';
import { Message } from '../classes/message';
import { Observable, throwError } from 'rxjs';
import { UsersService } from './users.service';
import { catchError , retry } from 'rxjs/internal/operators';
import { HttpHeaders,HttpClient } from '@angular/common/http';
import { DeviceService } from './device.service';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { GlobalSearchService } from './globalsearchservice.service';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';
import { GlPreviewResponse } from '../models/gl-preview.model';

/**
 * One line of the bookkeeper's check log — a payment known before it reaches the
 * bank. `transnos` names the documents the check is for, so posting can target
 * them explicitly instead of letting oldest-first allocation pick.
 */
export interface ExpectedPaymentInput {
	id?: number;
	debtorno: string;
	branchcode?: string;
	cust_name?: string;
	amount: number;
	transnos?: number[];
	credit_transnos?: number[];
	instrument?: 'check' | 'ach' | 'cash' | 'other';
	check_no?: string;
	/** Date written on the paper line — usually the invoice date, not the deposit date. */
	log_date?: string;
	deposit_batch?: string;
	source?: 'photo' | 'manual' | 'ai';
	status?: 'expected' | 'keyed' | 'matched' | 'void';
	posted_banktransid?: number;
	posted_transno?: number;
	notes?: string;
}

/** Count payment lines in postings object/array (matches server / getFinalizePayloadFromPreview). */
export function paymentRunPostingsCount(postings: any): number {
	if (postings == null || typeof postings !== 'object') {
		return 0;
	}
	return Array.isArray(postings) ? postings.length : Object.keys(postings).length;
}

export function hasPaymentRunPostings(postings: any): boolean {
	return paymentRunPostingsCount(postings) > 0;
}

/** User-visible line after a payment run is posted (checkpostfinalize response). */
export function formatPaymentRunPostedMessage(res: {
	journal?: number | string;
	summary?: { totalAmount?: number; currency?: string; vendorCount?: number; paymentCount?: number };
}): string {
	const j = res?.journal;
	const s = res?.summary;
	if (s && typeof s.totalAmount === 'number' && s.currency) {
		const amt = Number(s.totalAmount).toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		});
		const vc = Number(s.vendorCount) || 0;
		const pc = Number(s.paymentCount) || vc || 0;
		if (vc > 0 && pc > 0) {
			return `Posted run #${j}: ${s.currency} ${amt} (${pc} payment${pc === 1 ? '' : 's'}, ${vc} vendor${vc === 1 ? '' : 's'}).`;
		}
		return `Posted run #${j}: ${s.currency} ${amt}.`;
	}
	return j != null && String(j) !== '' ? `Posted payment run #${j}.` : 'Payment run posted.';
}

@Injectable({
	providedIn: 'root'
})
export class PaymentsService {
	key = 'UET6DFMPAXW7BKCB';
	config: any;
	//todo make connection reusabel
	socket: any;
	details: any;
	user : any = [];
	package: any = '';

	public httpOptions = {
		headers: new HttpHeaders({
			'Content-Type':	'application/json',
		})
	};
	private updateHeaders() {
		const uuid = this.deviceService.getDeviceUUID();
		let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		if (this.config && this.config.apiCredentials && this.config.apiCredentials.clientId && this.user && this.user.user && this.user.user.userid) {
			headers = headers.set('Authorization', 'Bearer ' + this.config.apiCredentials.clientId + '_' + this.user.user.userid);
		}
		headers = headers.set('X-Device-UUID', uuid);
		this.httpOptions.headers = headers;
	}

	constructor(private http: HttpClient,private _configSvc: RuntimeConfigLoaderService, private globalSearchService: GlobalSearchService, private deviceService: DeviceService) {
		this.globalSearchService.configsubscription.subscribe(r => {
			this.config = r;
			this.updateHeaders();
		})
		this.globalSearchService.user.subscribe( (result) => {
			this.user = result;
			this.updateHeaders();
		});

		this.updateHeaders();
	}

	public openJournal(data: any) {
		const method = 'payments/journal/open';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public closeJournal(data: any) {
		const method = 'payments/journal/close';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getJournals(data: any) {
		const method = 'payments/journal/getopenjournals';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getAllUserOpenJounrals(data: any) {
		const method = 'payments/journal/getalluseropenjournals';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}



	public getJournal(data: any) {
		const method = 'payments/journal/getopenjournal';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getAllJournals(data: any) {
		const method = 'payments/journal/getalljournals';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getClosedJournals(data: any) {
		const method = 'payments/journal/getclosedjournals';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}
	//first mile links -- sorry
	public getLinks(data: any) {
		const method = 'payments/postback/links';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}
	public removeCreditCard(data: any) {
		const method = 'payments/creditcards/remove';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public voidPayment(data: any) {
		const method = 'payments/void';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public voidInvoice(data: any) {
		const method = 'payments/voidinvoice';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	// Cross-ledger AR/AP payment for vendor-backed methods (Visa / MC / Amex /
	// Discover / Goodyear / Pep Boys / Synchrony etc). Server endpoint posts
	// the AR receipt + AP vendor credit + GL entries atomically and writes a
	// shared NA-{AR#}-{AP#}-{hex} reference on both sides.
	//
	// Expected payload:
	//   { payment: <standard payment fields>,
	//     national_account: { vendor_id, gl_account, credit_amount, vendor_reference, invoice? },
	//     from: [], to: [] }
	public nationalAccountPayment(data: any) {
		const method = 'payments/nationalaccount';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	// Vendor-backed remittance batch (Phase 2)
	public previewRemittance(data: any) {
		const method = 'payments/remittance/preview';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}
	public commitRemittance(data: any) {
		const method = 'payments/remittance/commit';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	// Vendor-backed receivables — accounting view (Phase 3)
	public getVendorBackedBalances() {
		const method = 'payments/vendor_backed/balances';
		return this.http.get(this.setEndPoint(method), this.httpOptions);
	}
	public getVendorBackedDetail(vendor_id: string) {
		const method = 'payments/vendor_backed/detail&vendor_id=' + encodeURIComponent(vendor_id);
		return this.http.get(this.setEndPoint(method), this.httpOptions);
	}
	public applyVendorBackedDeposit(data: any) {
		const method = 'payments/vendor_backed/applydeposit';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	// A/R Collections — collection notes / promise-to-pay tracker
	public getCollectionTracker(params: any = {}) {
		const qs = Object.keys(params)
			.filter(k => params[k] !== null && params[k] !== undefined && params[k] !== '')
			.map(k => '&' + k + '=' + encodeURIComponent(params[k]))
			.join('');
		return this.http.get(this.setEndPoint('receivables/collections/tracker' + qs), this.httpOptions);
	}
	public getCollectionCustomer(debtorno: string) {
		const method = 'receivables/collections/customer&debtorno=' + encodeURIComponent(debtorno);
		return this.http.get(this.setEndPoint(method), this.httpOptions);
	}
	public saveCollectionNote(data: any) {
		const method = 'receivables/collections/save';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('receivables', data))
		);
	}
	public removeCollectionNote(id: number) {
		const method = 'receivables/collections/remove';
		return this.http.post(this.setEndPoint(method), { id }, this.httpOptions).pipe(
			catchError(this.handleError('receivables', { id }))
		);
	}

	// Vendor-backed payments report (Phase 4)
	public getVendorBackedReport(params: any) {
		const qs = Object.keys(params)
			.map(k => '&' + k + '=' + encodeURIComponent(params[k]))
			.join('');
		const method = 'reports/vendor_backed' + qs;
		return this.http.get(this.setEndPoint(method), this.httpOptions);
	}

	public editCreditCard(data: any) {
		const method = 'payments/creditcards/edit';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public addCreditCard(data: any) {
		const method = 'payments/creditcards/add';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getStripeClientConfig() {
		const method = 'payments/creditcards/stripeconfig';
		return this.http.get(this.setEndPoint(method)).pipe(
			catchError(this.handleError('payments/creditcards/stripeconfig', {} as any))
		);
	}

	public loadProfile(debtorno: any, branchcode: any) {
		const method = 'payments/customerar/loadprofile&debtorno='+debtorno+'&branchcode='+branchcode;
		return this.http.get(this.setEndPoint(method));
	}

	public addAllocation(data: any) {
		const method = 'payments/allocations/addallocation';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public addAllocations(data: any) {
		const method = 'payments/addallocations';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getDayEndExcel(data: any) {
		const method = 'globals/dayend/excel';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getAmountSearch(data: any) {
		const method = 'payments/search/amount&amount='+data;
		return this.http.get(this.setEndPoint(method));
	}

	public getInvoiceSearch(data: any) {
		const method = 'payments/search/invoice&invoice='+data;
		return this.http.get(this.setEndPoint(method));
	}


	public getVendorAccountPayable(supplierid: any) {
		const method = 'payments/vendorap&supplierid='+supplierid;
		return this.http.get(this.setEndPoint(method));
	}

	public getVendorAccountPayables(supplierid: string) {
		const method = 'vendor/payments/getaccountpayables';
		const data = { supplierid: supplierid };
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('getVendorAccountPayables', data))
		);
	}

	public addVendorPaymentAllocations(data: any) {
		const method = 'vendor/payments/addallocations';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('addVendorPaymentAllocations', data))
		);
	}

	/**
	 * @param brands opt in to brand tagging on the returned documents (MOOV payment entry).
	 *               Off by default — the extra lookups are not free and no other screen
	 *               reads brands off this endpoint.
	 */
	public getCustomerAccountReceivables(debtorno: any, branchcode: any, brands = false) {
		let method = 'payments/customerar&debtorno='+debtorno+'&branchcode='+branchcode;
		if (brands) {
			method += '&brands=1';
		}
		return this.http.get(this.setEndPoint(method));
	}

	// Bulk customer remittance intake: parse -> review -> post one receipt + allocations.
	public remitParse(data: any) {
		const method = 'payments/remitparse';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('remitParse', data))
		);
	}

	public remitPost(data: any) {
		const method = 'payments/remitpost';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('remitPost', data))
		);
	}

	public remitGet(batchId: any) {
		const method = 'payments/remitget&batch_id='+batchId;
		return this.http.get(this.setEndPoint(method));
	}

	// Re-run matching on an existing batch (no re-upload) — used by the review "re-match" button.
	public remitRematch(data: any) {
		const method = 'payments/remitrematch';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('remitRematch', data))
		);
	}

	public remitLineDetail(lineId: any) {
		const method = 'payments/remitlinedetail&line_id='+lineId;
		return this.http.get(this.setEndPoint(method));
	}

	// AP remittance intake (vendor payment twin): parse -> review -> apply onto the
	// payment screen's allocations. Stateless — the client holds the batch and sends
	// its lines back for rematch/verify. Never posts money — see
	// apir/payments/ap_remittance.php.
	public apRemitParse(data: any) {
		const method = 'payments/ap_remittance/parse';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('apRemitParse', data))
		);
	}

	public apRemitRematch(data: any) {
		const method = 'payments/ap_remittance/rematch';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('apRemitRematch', data))
		);
	}

	// Plans how the credits the vendor netted out of the draft get paired to the
	// invoices it paid — read-only, capped at each document's live open balance.
	// The pairs it returns are handed to addVendorPayment() as credit_allocations.
	public apRemitCreditPlan(data: any) {
		const method = 'payments/ap_remittance/creditplan';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('apRemitCreditPlan', data))
		);
	}

	public apRemitLineDetail(data: any) {
		const method = 'payments/ap_remittance/linedetail';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('apRemitLineDetail', data))
		);
	}

	public getCustomerAccountReceivablesByTerm(shipvia: any) {
		const method = 'payments/customerar/term&shipvia='+shipvia;
		return this.http.get(this.setEndPoint(method));
	}
	public getCustomerAccountReceivablesByShipVia(shipvia: any) {
		const method = 'payments/customerar/shipvia&shipvia='+shipvia;
		return this.http.get(this.setEndPoint(method));
	}

	public autoAllocDocument(data:any) {
		const method = 'payments/allocations/autoalloc';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}
	public autoAllocMatchingDocument(data:any) {
		const method = 'payments/allocations/autoallocmatching';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}



	public dealllocateDocument(data:any) {
		const method = 'payments/allocations/deallocate';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public dealllocateTo(data:any) {
		const method = 'payments/allocations/deallocateto';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public dealllocateFrom(data:any) {
		const method = 'payments/allocations/deallocatefrom';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}



	public getCustomerAllPaymentCredits(debtorno: any, branchcode: any) {
		const method = 'payments/customerar&debtorno='+debtorno+'&branchcode='+branchcode+'&show=allrec';
		return this.http.get(this.setEndPoint(method));
	}

	public moveJournal(data: any) {
		const method = 'payments/movejournal';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public updateBankTrans(data: any) {
		const method = 'payments/updatetype';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public createRefund(data: any) {
		const method = 'payments/reversal';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}
	public getPaymentById(id: any) {
		const method = 'payments/gettransaction&id='+id;
		return this.http.post(this.setEndPoint(method), id, this.httpOptions).pipe(
			catchError(this.handleError('payments', id))
		);
	}

	public getPaymentRange(data: any) {
		const method = 'payments/getpaymentrange';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getVendorPaymentRange(data: any) {
		const method = 'payments/getvendorpaymentrange';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}



	public updateTransaction(data: any) {
		const method = 'payments/updatebankrec';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	/**
	 * Clear/un-clear many rec rows in one call. Skips cleared_locked rows and reports
	 * them back, so a batch can never silently half-apply — which is what one
	 * updateTransaction per row cannot tell you.
	 */
	public batchUpdateBankRec(rows: { banktransid: number; amountCleared: number }[]) {
		const data = { rows };
		return this.http.post(this.setEndPoint('payments/batchupdatebankrec'), data, this.httpOptions).pipe(
			catchError(this.handleError('batchupdatebankrec', data))
		);
	}

	public lockBankRec(data: { banktransid: number }) {
		const method = 'payments/lockbankrec';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public unlockBankRec(data: { banktransid: number }) {
		const method = 'payments/unlockbankrec';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getBankRec(data: any) {
		const method = 'payments/getbankrec';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getBankGlBook(data: { bankAccount: string; asOf?: string | Date | null }) {
		const method = 'payments/getbankglbook';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getDailyTransactions(data: any) {
		const method = 'payments/gettransactions';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getDRI(data: any) {
		const method = 'payments/getdri';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}


	public getCheckSetup(payments: any) {
		const method = 'apchecksma';
		const data = payments
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	/** Checklist search: requires supplierIds (string[]), dueDate, currency, etc. */
	public searchChecks(payments: any) {
		const method = 'apchecksma/getchecks';
		const data = payments
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public postChecks(payments: any) {
		const method = 'apchecksma/checkpost';
		const data = payments
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public postChecksFinalize(payments: any) {
		const method = 'apchecksma/checkpostfinalize';
		const data = payments
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	/** Chunked posting — sends a slice of postings to the server, returns per-chunk result. */
	public postChecksChunk(payload: {
		journal: string | number;
		chunk: any[];
		startIndex: number;
		totalCount: number;
		isLast: boolean;
	}) {
		const method = 'apchecksma/checkpostchunk';
		return this.http.post(this.setEndPoint(method), payload, this.httpOptions).pipe(
			catchError(this.handleError('postChecksChunk', payload))
		);
	}

	/** Render one already-posted vendor payment as a check PDF (base64). Posts nothing. */
	public getSingleCheckPdf(payload: any) {
		const method = 'apchecksma/singlecheckpdf';
		return this.http.post(this.setEndPoint(method), payload, this.httpOptions).pipe(
			catchError(this.handleError('getSingleCheckPdf', payload))
		);
	}

	public savePaymentRunForReview(payload: any) {
		const method = 'apchecksma/saveforreview';
		return this.http.post(this.setEndPoint(method), payload, this.httpOptions).pipe(
			catchError(this.handleError('savePaymentRunForReview', payload))
		);
	}

	public getPendingRuns() {
		const method = 'apchecksma/getpendingruns';
		return this.http.post(this.setEndPoint(method), {}, this.httpOptions).pipe(
			catchError(this.handleError('getPendingRuns', {}))
		);
	}

	public loadRunData(params: { journal: number | string }) {
		const method = 'apchecksma/loadrundata';
		return this.http.post(this.setEndPoint(method), params, this.httpOptions).pipe(
			catchError(this.handleError('loadRunData', params))
		);
	}

	public loadRunForEdit(params: { journal: number | string }) {
		const method = 'apchecksma/loadrunforedit';
		return this.http.post(this.setEndPoint(method), params, this.httpOptions).pipe(
			catchError(this.handleError('loadRunForEdit', params))
		);
	}

	public discardRun(params: { journal: number | string }) {
		const method = 'apchecksma/discardrun';
		return this.http.post(this.setEndPoint(method), params, this.httpOptions).pipe(
			catchError(this.handleError('discardRun', params))
		);
	}

	/** Fetch bank accounts for a vendor currency, optionally filtered by an override payment currency. */
	public getBankAccountsForCurrency(curr: string, overrideCurr: string | null = null) {
		const method = 'apchecksma/bankaccounts';
		const data: any = { curr };
		if (overrideCurr) {
			data.override_curr = overrideCurr;
		}
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('getBankAccountsForCurrency', data))
		);
	}

	public getPayments(date: any) {
		const method = 'payments/getpayments&date='+date;
		return this.http.get(this.setEndPoint(method));
	}

	public addVendorPayment(payments: any) {
		const method = 'vendor/payments/add';
		const data = payments
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public previewVendorPaymentGl(payload: any) {
		const method = 'vendor/payments/glpreview';
		return this.http.post<GlPreviewResponse>(this.setEndPoint(method), payload, this.httpOptions).pipe(
			catchError(this.handleError(method, payload))
		);
	}

	public getVendorRemittanceView() {
		const method = 'vendor/payments/remittanceview';
		return this.http.get(this.setEndPoint(method), this.httpOptions);
	}

	public saveVendorRemittanceEnabled(enabled: boolean) {
		const method = 'vendor/payments/remittancesave';
		return this.http.post(this.setEndPoint(method), { enabled }, this.httpOptions);
	}

	public previewVendorRemittanceLive(body: {
		supplier_id: string;
		context: Record<string, unknown>;
		allocations: Array<{ trans_id: number; amount: number }>;
	}) {
		const method = 'vendor/payments/remittancepreviewlive';
		return this.http.post(this.setEndPoint(method), body, this.httpOptions);
	}

	/**
	 * Rebuild remit preview for an already-posted vendor payment.
	 * The server pulls supplier + allocations from the payment id itself.
	 */
	public getVendorRemitByPaymentId(paymentId: number | string) {
		const method = 'vendor/payments/remitbypaymentid&payment_id=' + encodeURIComponent(String(paymentId));
		return this.http.get(this.setEndPoint(method));
	}

	public removePayment(payment_id: any) {
		const method = 'orders/payments/remove';
		const data = { payment_id: payment_id }
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public removeAllPayments(payload: any) {
		const method = 'orders/payments/removeall';
		const data = payload
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}



	public getOrderPayments(data: any) {
		const method = 'orders/payments/get';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getOrderGiftCards(data: any){
		const method = 'orders/payments/loadgiftcard';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getPosOrderPayments(data: any) {
		const method = 'orders/payments/get&pos=true';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public addCashRecPayment(data: any) {
		const method = 'payments/add';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public setDefaultCard(data: any) {
		const method = 'payments/creditcards/setdefault';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public addShipViaPayment(data: any) {
		const method = 'payments/shipvia';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public addPayment(data: any) {
		const method = 'orders/payments/add';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public updatePayment(data: any) {
		const method = 'orders/payments/update';
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	public getRefundables(debtorno: any) {
		const method = 'payments/refundable/getRefundables&debtorno='+debtorno;
		return this.http.get(this.setEndPoint(method), this.httpOptions);
	}

	/**
	 * Runs the vendor payment run report (crossPoint API).
	 * Payload: supplierIds (string[]), currency, exchange, fromaccount, paytype, process? (optional).
	 * Legacy fromvend/tovend still accepted by server only if supplierIds omitted.
	 * When filterByDueDate is true: include `due` (date) and filter payables by that due date.
	 * When filterByDueDate is false: omit `due`; backend should return all payables (no due-date filter).
	 */
	public runPaymentReport(input: any) {
		const method = 'vendor/payments/paymentrunreport';
		const data = input;
		return this.http.post(this.setEndPoint(method), data, this.httpOptions).pipe(
			catchError(this.handleError('payments', data))
		);
	}

	private getApi() {
		  return this.config.apiServer.crossPoint;
	}

	private getApiKey() {
		 return '&key='+this.config.apiCredentials.clientId+'&userid='+this.user.user.userid + this.getPackage();;
	}

	public getKey() {
		return '&key=' + this.config.apiCredentials.clientId + '&userid=' + this.user.user.userid + this.getPackage();
	}

	private getPackage() {
		const p = this.globalSearchService.getSessionItem('package');
		if(p) {
			return '&package='+p;
		} else {
			return '';
		}
	}

	// --- Bank-rec integrations: SimpleFIN bank feed ---
	public simplefinStatus() {
		return this.http.post(this.setEndPoint('integrations/simplefinStatus'), {}, this.httpOptions).pipe(
			catchError(this.handleError('simplefinStatus', {}))
		);
	}
	public simplefinClaim(token: string) {
		const data = { token };
		return this.http.post(this.setEndPoint('integrations/simplefinClaim'), data, this.httpOptions).pipe(
			catchError(this.handleError('simplefinClaim', data))
		);
	}
	public simplefinAccounts() {
		return this.http.post(this.setEndPoint('integrations/simplefinAccounts'), {}, this.httpOptions).pipe(
			catchError(this.handleError('simplefinAccounts', {}))
		);
	}
	public simplefinSetMap(accountMap: any) {
		const data = { accountMap };
		return this.http.post(this.setEndPoint('integrations/simplefinSetMap'), data, this.httpOptions).pipe(
			catchError(this.handleError('simplefinSetMap', data))
		);
	}
	public simplefinSync(range: { start?: string; end?: string }) {
		return this.http.post(this.setEndPoint('integrations/simplefinSync'), range, this.httpOptions).pipe(
			catchError(this.handleError('simplefinSync', range))
		);
	}
	public simplefinFeed(range: { start?: string; end?: string }) {
		return this.http.post(this.setEndPoint('integrations/simplefinFeed'), range, this.httpOptions).pipe(
			catchError(this.handleError('simplefinFeed', range))
		);
	}
	// Statement file import — the same bank feed, sourced from a dropped file
	// instead of a bridge connection. preview() writes nothing; commit() imports
	// only the rows the operator accepted.
	public statementPreview(data: { bankaccount: string; file: { name: string; content: string }; mapping?: any }) {
		return this.http.post(this.setEndPoint('integrations/statement/preview'), data, this.httpOptions).pipe(
			catchError(this.handleError('statementPreview', data))
		);
	}
	public statementCommit(data: { bankaccount: string; rows: any[]; source_name?: string; file?: { name: string; content: string }; balance?: { opening: number; closing: number } }) {
		return this.http.post(this.setEndPoint('integrations/statement/commit'), data, this.httpOptions).pipe(
			catchError(this.handleError('statementCommit', data))
		);
	}
	public statementSaveMapping(data: { bankaccount: string; mapping: any }) {
		return this.http.post(this.setEndPoint('integrations/statement/saveMapping'), data, this.httpOptions).pipe(
			catchError(this.handleError('statementSaveMapping', data))
		);
	}
	/** File-drop history for the "Statement files" screen — most recent first. */
	public statementHistory(data: { bankaccount?: string; limit?: number } = {}) {
		return this.http.post(this.setEndPoint('integrations/statement/history'), data, this.httpOptions).pipe(
			catchError(this.handleError('statementHistory', data))
		);
	}
	/** Re-read a past drop's stored bytes — same response shape as statementPreview. */
	public statementReload(data: { id: number }) {
		return this.http.post(this.setEndPoint('integrations/statement/reload'), data, this.httpOptions).pipe(
			catchError(this.handleError('statementReload', data))
		);
	}
	/** Reverse a drop: removes its feed rows and un-clears any banktrans it matched. */
	public statementUndo(data: { id: number }) {
		return this.http.post(this.setEndPoint('integrations/statement/undo'), data, this.httpOptions).pipe(
			catchError(this.handleError('statementUndo', data))
		);
	}
	public simplefinSaveMatches(payload: { matches: any[]; allow_shared?: boolean } | any[]) {
		const data = Array.isArray(payload) ? { matches: payload } : payload;
		return this.http.post(this.setEndPoint('integrations/simplefinSaveMatches'), data, this.httpOptions).pipe(
			catchError(this.handleError('simplefinSaveMatches', data))
		);
	}
	public simplefinUnmatch(row: { sf_account: string; sf_id: string }) {
		return this.http.post(this.setEndPoint('integrations/simplefinUnmatch'), row, this.httpOptions).pipe(
			catchError(this.handleError('simplefinUnmatch', row))
		);
	}
	public simplefinSetIgnore(row: { sf_account: string; sf_id: string; ignored: boolean }) {
		return this.http.post(this.setEndPoint('integrations/simplefinSetIgnore'), row, this.httpOptions).pipe(
			catchError(this.handleError('simplefinSetIgnore', row))
		);
	}

	public simplefinSetPosted(row: { sf_account: string; sf_id: string; post_type: number; post_transno: number; post_kind: string }) {
		return this.http.post(this.setEndPoint('integrations/simplefinSetPosted'), row, this.httpOptions).pipe(
			catchError(this.handleError('simplefinSetPosted', row))
		);
	}
	public simplefinDisconnect() {
		return this.http.post(this.setEndPoint('integrations/simplefinDisconnect'), {}, this.httpOptions).pipe(
			catchError(this.handleError('simplefinDisconnect', {}))
		);
	}
	public simplefinAiMatch(payload: { bankRows: any[]; sfRows: any[] }) {
		return this.http.post(this.setEndPoint('integrations/simplefinAiMatch'), payload, this.httpOptions).pipe(
			catchError(this.handleError('simplefinAiMatch', payload))
		);
	}
	/** Read-only AR proposals over unmatched bank deposits (ready/review/unresolved). */
	public simplefinProposeReceipts(range: { start?: string; end?: string }) {
		return this.http.post(this.setEndPoint('integrations/simplefinProposeReceipts'), range, this.httpOptions).pipe(
			catchError(this.handleError('simplefinProposeReceipts', range))
		);
	}
	/** Oldest-first allocation plan (to[] for payments/add) for one deposit. Also returns unmatched keyed receipts for Match. */
	public simplefinReceiptPlan(payload: {
		debtorno: string; branchcode: string; amount: number; transnos?: number[]; credit_transnos?: number[];
		/** Deposit posted date (Y-m-d) — windows the unmatched-receipt search. */
		posted?: string;
	}) {
		return this.http.post(this.setEndPoint('integrations/simplefinReceiptPlan'), payload, this.httpOptions).pipe(
			catchError(this.handleError('simplefinReceiptPlan', payload))
		);
	}
	/** Remember payer → customer so recurring ACH senders auto-resolve next import. */
	public simplefinLearnAlias(payload: { description?: string; payer?: string; debtorno: string; branchcode: string; cust_name?: string }) {
		return this.http.post(this.setEndPoint('integrations/simplefinLearnAlias'), payload, this.httpOptions).pipe(
			catchError(this.handleError('simplefinLearnAlias', payload))
		);
	}

	/* ---------- batch (N ERP rows : 1 bank line) matching ---------- */

	/** Read-only group proposals for one bank account — nothing is matched here. */
	public simplefinProposeGroups(payload: { bankaccount: string; start?: string; end?: string; sf_id?: string; limit?: number }) {
		return this.http.post(this.setEndPoint('integrations/simplefinProposeGroups'), payload, this.httpOptions).pipe(
			catchError(this.handleError('simplefinProposeGroups', payload))
		);
	}

	/** Read (omit save) or save the per-account grouping rules. */
	public simplefinGroupSettings(payload: {
		bankaccount: string; save?: boolean; offset_accounts?: string[];
		lag_days_back?: number; lag_days_fwd?: number; variance_abs?: number; variance_pct?: number;
		tender_groups?: { label: string; types: string[] }[];
		/** {type: dollars} — per-tender over/short allowance (cash drawers). */
		type_variances?: { [type: string]: number };
	}) {
		return this.http.post(this.setEndPoint('integrations/simplefinGroupSettings'), payload, this.httpOptions).pipe(
			catchError(this.handleError('simplefinGroupSettings', payload))
		);
	}

	/* ---------- expected payments ("the check log") ---------- */

	/** Log lines still live (expected/keyed), with the live balance of each target invoice. */
	public expectedPaymentsList(filter: { status?: string[]; deposit_batch?: string; debtorno?: string } = {}) {
		return this.http.post(this.setEndPoint('integrations/expectedPaymentsList'), filter, this.httpOptions).pipe(
			catchError(this.handleError('expectedPaymentsList', filter))
		);
	}
	/** Bulk upsert — one call carries a whole photographed log page. Rows with an id are updated. */
	public expectedPaymentsSave(payload: { rows: ExpectedPaymentInput[] }) {
		return this.http.post(this.setEndPoint('integrations/expectedPaymentsSave'), payload, this.httpOptions).pipe(
			catchError(this.handleError('expectedPaymentsSave', payload))
		);
	}
	public expectedPaymentsDelete(payload: { id: number; void?: boolean }) {
		return this.http.post(this.setEndPoint('integrations/expectedPaymentsDelete'), payload, this.httpOptions).pipe(
			catchError(this.handleError('expectedPaymentsDelete', payload))
		);
	}
	/** Record that a log line's receipt has been posted (status expected → keyed). */
	public expectedPaymentsKeyed(payload: { id: number; transno: number }) {
		return this.http.post(this.setEndPoint('integrations/expectedPaymentsKeyed'), payload, this.httpOptions).pipe(
			catchError(this.handleError('expectedPaymentsKeyed', payload))
		);
	}
	/**
	 * Photo of the handwritten check log → reviewable rows. Reads nothing into the
	 * database: the server resolves each order number to its invoice and flags any
	 * row whose logged amount disagrees, then the operator saves what's correct.
	 */
	public expectedPaymentsFromImage(payload: { image_base64: string; media_type?: string }) {
		return this.http.post(this.setEndPoint('integrations/expectedPaymentsFromImage'), payload, this.httpOptions).pipe(
			catchError(this.handleError('expectedPaymentsFromImage', payload))
		);
	}
	/** Tie a log line to the feed row that turned out to be it; pairs an already-keyed receipt. */
	public expectedPaymentsResolve(payload: { id: number; sf_account: string; sf_id: string; banktransid?: number }) {
		return this.http.post(this.setEndPoint('integrations/expectedPaymentsResolve'), payload, this.httpOptions).pipe(
			catchError(this.handleError('expectedPaymentsResolve', payload))
		);
	}

	private setEndPoint(method: any) {
		  return this.getApi()+method+this.getApiKey()
	}

	private handleError<T>(operation = 'operation', result?: T) {
		return (error: any): Observable<T> => {
			return throwError(() => error);
		};
	}
}
